/**
 * Product Analytics Identity Hook
 *
 * Synchronizes user, corporate, and site context with the product analytics provider.
 * This ensures all analytics events (auto-captured and manual) include
 * the correct context for analytics and support.
 *
 * Currently uses PostHog, but abstracted for future-proofing.
 *
 * Calls:
 * - productAnalytics.identify() to associate events with a user
 * - productAnalytics.group() to associate with corporate and site
 * - productAnalytics.register() to set super properties sent with every event
 *
 * Usage:
 * ```tsx
 * // In your app's root component (after AuditTrackerConfigProvider)
 * function App() {
 *   useProductAnalyticsIdentity()
 *   return <Routes />
 * }
 * ```
 */

import { useEffect, useRef } from 'react'
import { productAnalytics, type ProductAnalyticsClient } from '../services/productAnalytics'
import { useAuditTrackerConfigOptional } from './useAuditTrackerConfig'
import { getDeviceType, getBrowserInfo, getOSInfo } from '../utils/device-info'
import { buildSiteGroupProperties, shouldSendSiteGroup } from './product-analytics-identity.utils'

// =============================================================================
// Types
// =============================================================================

export interface ProductAnalyticsIdentityOptions {
  /** Whether to include user roles in properties (default: true) */
  includeRoles?: boolean
  /** Whether to include email domain (default: true) */
  includeEmailDomain?: boolean
  /** Whether to include full email for staff users (default: true for web apps) */
  includeEmail?: boolean
  /** Whether to include device info (default: true) */
  includeDeviceInfo?: boolean
  /** Custom analytics client (for testing or alternative providers) */
  analyticsClient?: ProductAnalyticsClient
}

interface DeviceInfo {
  device_type: 'mobile' | 'tablet' | 'desktop'
  browser: string
  browser_version: string
  os: string
  os_version: string
  screen_width: number
  screen_height: number
  viewport_width: number
  viewport_height: number
  language: string
  timezone: string
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Extract domain from email address
 * e.g., "user@hapana.com" -> "hapana.com"
 */
function getEmailDomain(email: string | undefined): string | undefined {
  if (!email) {
    return undefined
  }
  const parts = email.split('@')
  return parts.length === 2 ? parts[1] : undefined
}

function getDeviceInfo(): DeviceInfo {
  const browser = getBrowserInfo()
  const os = getOSInfo()

  return {
    device_type: getDeviceType(),
    browser: browser.name,
    browser_version: browser.version,
    os: os.name,
    os_version: os.version,
    screen_width: typeof screen !== 'undefined' ? screen.width : 0,
    screen_height: typeof screen !== 'undefined' ? screen.height : 0,
    viewport_width: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewport_height: typeof window !== 'undefined' ? window.innerHeight : 0,
    language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || 'unknown',
  }
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Synchronizes user/corporate/site context with the product analytics provider
 *
 * This hook should be called once in your app's root component,
 * after AuditTrackerConfigProvider has been set up.
 *
 * @param options Configuration options
 */
export function useProductAnalyticsIdentity(options: ProductAnalyticsIdentityOptions = {}): void {
  const {
    includeRoles = true,
    includeEmailDomain = true,
    includeEmail = true,
    includeDeviceInfo = true,
    analyticsClient = productAnalytics,
  } = options

  const config = useAuditTrackerConfigOptional()

  // Track what we've already identified to avoid redundant calls
  const lastIdentifiedUser = useRef<string | null>(null)
  const lastCorporateId = useRef<string | null>(null)
  const lastSiteId = useRef<string | null>(null)
  const lastSiteCorporateId = useRef<string | null>(null)

  useEffect(() => {
    // Bail if analytics isn't available
    if (!analyticsClient.isEnabled()) {
      return
    }

    // Bail if we don't have config yet
    if (!config) {
      return
    }

    const {
      userId,
      userEmail,
      userFirstName,
      userLastName,
      corporateId,
      corporateSlug,
      corporateName,
      siteId,
      siteName,
      siteSlug,
      userRoles,
      source,
    } = config

    // ==========================================================================
    // Identify User
    // ==========================================================================
    if (userId && userId !== lastIdentifiedUser.current) {
      const userProperties: Record<string, unknown> = {}

      if (includeEmail && userEmail) {
        userProperties.email = userEmail
      }
      const fullName = [userFirstName, userLastName].filter(Boolean).join(' ')
      if (includeEmail && fullName) {
        userProperties.name = fullName
      }

      if (includeEmailDomain && userEmail) {
        const emailDomain = getEmailDomain(userEmail)
        if (emailDomain) {
          userProperties.email_domain = emailDomain
        }
      }

      // Add roles
      if (includeRoles && userRoles?.length) {
        userProperties.roles = userRoles
        userProperties.primary_role = userRoles[0]
      }

      // Add source app
      userProperties.source_app = source

      // Non-PII tenant key: lets person-aggregated feature flags target a whole
      // corporate (the sandbox pipeline enrols sandboxes by this property).
      if (corporateId) {
        userProperties.corporate_id = corporateId
      }

      analyticsClient.identify(userId, userProperties)
      lastIdentifiedUser.current = userId
    }

    // ==========================================================================
    // Set Corporate Group
    // ==========================================================================
    if (corporateId && corporateId !== lastCorporateId.current) {
      analyticsClient.group('corporate', corporateId, {
        name: corporateName,
        slug: corporateSlug,
      })
      lastCorporateId.current = corporateId
    }

    // ==========================================================================
    // Set Site Group
    // ==========================================================================
    const siteIdentity = { siteId, siteName, siteSlug, corporateId }
    if (
      siteId &&
      shouldSendSiteGroup(siteIdentity, {
        siteId: lastSiteId.current,
        corporateId: lastSiteCorporateId.current,
      })
    ) {
      analyticsClient.group('site', siteId, buildSiteGroupProperties(siteIdentity))
      lastSiteId.current = siteId
      lastSiteCorporateId.current = corporateId || null
    }

    // ==========================================================================
    // Register Super Properties (sent with every event)
    // ==========================================================================
    const superProperties: Record<string, unknown> = {
      // Corporate context
      corporate_id: corporateId || undefined,
      corporate_slug: corporateSlug || undefined,
      corporate_name: corporateName || undefined,

      // Site context
      site_id: siteId || undefined,
      site_name: siteName || undefined,
      site_slug: siteSlug || undefined,

      // User context (non-PII)
      user_id: userId || undefined,
      source_app: source,
    }

    // Add email domain
    if (includeEmailDomain && userEmail) {
      superProperties.email_domain = getEmailDomain(userEmail)
    }

    // Add roles
    if (includeRoles && userRoles?.length) {
      superProperties.user_roles = userRoles
      superProperties.primary_role = userRoles[0]
    }

    // Add device info
    if (includeDeviceInfo) {
      const deviceInfo = getDeviceInfo()
      Object.assign(superProperties, {
        device_type: deviceInfo.device_type,
        browser: deviceInfo.browser,
        browser_version: deviceInfo.browser_version,
        os: deviceInfo.os,
        os_version: deviceInfo.os_version,
        screen_width: deviceInfo.screen_width,
        screen_height: deviceInfo.screen_height,
        viewport_width: deviceInfo.viewport_width,
        viewport_height: deviceInfo.viewport_height,
        language: deviceInfo.language,
        timezone: deviceInfo.timezone,
      })
    }

    // Remove undefined values
    const cleanProperties = Object.fromEntries(
      Object.entries(superProperties).filter(([_, v]) => v !== undefined)
    )

    analyticsClient.register(cleanProperties)
  }, [config, includeRoles, includeEmailDomain, includeEmail, includeDeviceInfo, analyticsClient])
}

/**
 * Reset analytics identity (call on logout)
 */
export function resetProductAnalyticsIdentity(
  client: ProductAnalyticsClient = productAnalytics
): void {
  client.reset()
}

export default useProductAnalyticsIdentity
